/**
 * ADLSupervisorModal
 * CCH Care — Phase 3 + 5
 *
 * Changes:
 * - Warning banner when signedSections < 9
 * - Confirm button disabled when signedSections < 9 (backend enforces this too)
 *
 * Changes (2026-04-18):
 * - Updated section count from 8 to 9 — Phase 5 added Vitals as the 9th
 *   ADL section. Backend `signatures.php` already requires 9 for supervisor
 *   sign-off; this modal was out of sync and would have shown "all signed"
 *   at 8, then the Approve button would have failed with a 422 from the API.
 */

import { useState } from 'react';
import { COLORS } from '../../config/constants';

const TOTAL_SECTIONS = 9;

export default function ADLSupervisorModal({
  isOpen,
  residentName,
  shiftType,
  shiftDate,
  signedSections = 0,
  supervisorName,
  signing = false,
  error = null,
  onConfirm,
  onCancel,
}) {
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const allSigned       = signedSections >= TOTAL_SECTIONS;
  const missingSections = Math.max(0, TOTAL_SECTIONS - signedSections);
  const canConfirm      = allSigned && !signing;

  function handleConfirm() {
    if (!canConfirm) return;
    onConfirm?.(notes.trim() || null);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCancel}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onCancel?.(); }}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        background: COLORS.surface,
        borderRadius: '16px 16px 0 0',
        padding: '24px 20px 36px',
        zIndex: 1001,
        maxWidth: 480,
        margin: '0 auto',
      }}>
        {/* Handle */}
        <div style={{
          width: 36, height: 4, background: COLORS.border,
          borderRadius: 999, margin: '0 auto 20px',
        }} />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔍</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
            Supervisor Review
          </div>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 6 }}>
            Review and co-sign this ADL record to mark it as supervisor-reviewed.
          </div>
        </div>

        {/* Incomplete sections warning */}
        {!allSigned && (
          <div style={{
            background: COLORS.dangerLight,
            border: `1px solid ${COLORS.dangerAlpha}`,
            borderRadius: 10, padding: '12px 16px',
            marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.danger, marginBottom: 4 }}>
                {missingSections} section{missingSections > 1 ? 's' : ''} not yet signed
              </div>
              <div style={{ fontSize: 12, color: COLORS.danger }}>
                All {TOTAL_SECTIONS} sections must be signed or marked N/A before supervisor co-sign. Ask the CNA to complete the remaining section{missingSections > 1 ? 's' : ''} first.
              </div>
            </div>
          </div>
        )}

        {/* Record summary */}
        <div style={{
          background: COLORS.bg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 10, padding: '14px 16px',
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>RESIDENT</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{residentName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>SHIFT</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{shiftType} — {shiftDate}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>SECTIONS SIGNED</span>
            <span style={{
              fontSize: 13, fontWeight: 700,
              color: allSigned ? COLORS.accent : COLORS.danger,
            }}>
              {signedSections}/{TOTAL_SECTIONS} {allSigned ? '✓' : '⚠️'}
            </span>
          </div>
        </div>

        {/* Supervisor identity */}
        <div style={{
          background: COLORS.primaryLight,
          border: `1px solid ${COLORS.primaryAlpha}`,
          borderRadius: 10, padding: '12px 16px',
          marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 20 }}>👤</span>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600 }}>REVIEWING AS</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>{supervisorName}</div>
          </div>
        </div>

        {/* Notes — only shown when all sections are signed */}
        {allSigned && (
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="supervisor-review-notes" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 6 }}>
              Review Notes (optional)
            </label>
            <textarea
              id="supervisor-review-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add supervisor review notes…"
              rows={2}
              style={{
                width: '100%', padding: '10px 12px',
                border: `1px solid ${COLORS.border}`, borderRadius: 8,
                fontSize: 14, color: COLORS.text, resize: 'none',
                boxSizing: 'border-box', fontFamily: 'inherit',
                background: COLORS.surface,
              }}
            />
          </div>
        )}

        {/* API error */}
        {error && (
          <div style={{
            background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerAlpha}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            fontSize: 13, color: COLORS.danger, fontWeight: 600,
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={signing}
            style={{
              flex: 1, padding: '12px 0',
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 10, fontSize: 14, fontWeight: 600,
              color: COLORS.textMuted, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              flex: 2, padding: '12px 0',
              background: canConfirm ? COLORS.primary : COLORS.border,
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 700,
              color: canConfirm ? '#fff' : COLORS.textMuted,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              transition: 'background 0.2s',
            }}
          >
            {signing ? 'Submitting…' : allSigned ? 'Approve & Co-Sign 🔍' : 'Sections Incomplete'}
          </button>
        </div>
      </div>
    </>
  );
}
